import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Flex } from '$components/layouts';
import { SvgIcon } from '$components/elements/svg-icon';
import { Text } from '$components/elements/text';
import { ensureEnding } from '$lib/ensure-ending';
import { IEnrichedProduct, IExpresseDeliveryResponse, IProductPage } from '$models';
import { B2CDeliveryMethod, ModalTrackingTypes } from '$constants';
import { UspItem, DeliveryUsp } from './components';
import { useB2CCorporateUser, useTranslation } from '$hooks';
import { SidePanel } from '$components/elements/side-panel';
import { RichText } from '$components/elements/rich-text';
import { Image } from '$components/elements/image';
import { theme } from '$theme';
import { ProductDeliveryOptions } from '../product-delivery-options';

type Props = {
    variant?: IEnrichedProduct;
    deliveryText?: string;
    shopInformation?: string;
    pageContent?: IProductPage;
    selectedZipCode?: string;
    sameDayDeliveryData?: IExpresseDeliveryResponse | null;
    sameDayDeliveryEnabled?: boolean;
    validatingSameDayDelivery?: boolean;
    onZipCodeChange?: (zipCode: string) => void;
};

export const ProductUsps = ({
    variant,
    deliveryText,
    shopInformation,
    pageContent,
    selectedZipCode,
    sameDayDeliveryData,
    validatingSameDayDelivery,
    sameDayDeliveryEnabled,
    onZipCodeChange,
}: Props) => {
    const [showDeliveryInformation, setShowDeliveryInformation] = useState<boolean>(false);
    const [showStoreInformation, setShowStoreInformation] = useState<boolean>(false);
    const { translate } = useTranslation();

    const [showModal, setShowModal] = useState(false);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [modalContent, setModalContent] = useState<string>();
    const [modalTitle, setModalTitle] = useState<string>();
    const [modalTrackType, setModalTrackType] = useState<string>();
    const { isLoading, corporateInfo } = useB2CCorporateUser();

    const showSameDayDelivery =
        !corporateInfo?.b2CCorporateUser &&
        sameDayDeliveryEnabled &&
        !!Object.values(variant?.stockStatusForShops ?? {}).some((stock) => stock > 0) &&
        ![
            B2CDeliveryMethod.DirectOnly,
            B2CDeliveryMethod.PreorderDirectOnly,
            B2CDeliveryMethod.ShopOnly,
            B2CDeliveryMethod.PreorderShopOnly,
        ].includes(variant?.b2CDeliveryMethod ?? -1);

    const onOpenModal = (richText?: string, title?: string, modalTrackType?: string) => {
        setModalTrackType(modalTrackType);
        setModalContent(richText);
        setShowModal(true);
        setModalTitle(title);
    };

    const showProductInformation = (deliveryMethod?: number) => {
        switch (deliveryMethod) {
            case B2CDeliveryMethod.Preorder:
            case B2CDeliveryMethod.PreorderShopAndDirect:
            case B2CDeliveryMethod.ShopAndDirect:
            case B2CDeliveryMethod.AvailableSoon:
                setShowDeliveryInformation(true);
                setShowStoreInformation(true);
                break;
            case B2CDeliveryMethod.DirectOnly:
            case B2CDeliveryMethod.PreorderDirectOnly:
                setShowDeliveryInformation(true);
                setShowStoreInformation(false);
                break;
            case B2CDeliveryMethod.ShopOnly:
            case B2CDeliveryMethod.PreorderShopOnly:
            case B2CDeliveryMethod.NotPossible:
                setShowDeliveryInformation(false);
                setShowStoreInformation(true);
                break;
            case B2CDeliveryMethod.Download:
                if (variant?.isReleased !== undefined && !variant?.isReleased) {
                    setShowDeliveryInformation(true);
                } else {
                    setShowDeliveryInformation(false);
                }
                setShowStoreInformation(true);
                break;
            default:
                setShowDeliveryInformation(true);
                setShowStoreInformation(true);
                break;
        }
    };

    useEffect(() => {
        showProductInformation(variant?.b2CDeliveryMethod);
    }, [variant]);

    const onDeliveryUspClick = () => {
        setShowDeliveryModal(true);
    };

    if (isLoading) return null;

    return (
        <>
            {showSameDayDelivery && (
                <Container borderColor={theme.colors.secondaryBlueDarkBase}>
                    <DeliveryUsp
                        onClick={onDeliveryUspClick}
                        selectedZipCode={selectedZipCode}
                        itemNumber={variant?.product?.itemNumber}
                        deliveryMethod={variant?.b2CDeliveryMethod}
                        sameDayDeliveryData={sameDayDeliveryData}
                    />
                </Container>
            )}
            <Container smallMargin>
                {showDeliveryInformation && !!deliveryText && (
                    <UspItem
                        onClick={() =>
                            onOpenModal(
                                pageContent?.deliveryInformation,
                                translate('productPage.productDetails.modalDeliveryInformationTitle'),
                                ModalTrackingTypes.DeliveryInfo
                            )
                        }
                    >
                        <IconContainer>
                            <SvgIcon svg="package" color="transparent" size={24} />
                        </IconContainer>
                        <TextContainer column flex>
                            <Text fontWeight="Medium">{ensureEnding(deliveryText)}</Text>
                        </TextContainer>
                    </UspItem>
                )}
                {showStoreInformation && !!shopInformation && (
                    <UspItem>
                        <IconContainer>
                            <SvgIcon svg="click" size={24} />
                        </IconContainer>
                        <TextContainer column flex>
                            <Text fontWeight="Medium">{ensureEnding(shopInformation)}</Text>
                        </TextContainer>
                    </UspItem>
                )}
                {variant?.product?.itemPriceTagUrl && !variant?.product?.itemPriceTagLinkUrl && (
                    <UspItem
                        onClick={() =>
                            onOpenModal(
                                pageContent?.itemPriceTagInformation,
                                translate('productPage.productDetails.modalReadMoreSplashInformationTitle'),
                                ModalTrackingTypes.ReadMoreSplashes
                            )
                        }
                    >
                        <IconContainer>
                            <Image src={variant.product.itemPriceTagUrl} />
                        </IconContainer>
                        <TextContainer column flex>
                            <Text fontWeight="Medium">{variant.product.itemPriceTagValue}</Text>
                            <Text variant="caption" color={theme.colors.textSubtle} fontWeight="Medium">{`${translate(
                                'productPage.productDetails.readMoreSplashLbl'
                            )} ${variant.product.itemPriceTagValue}`}</Text>
                        </TextContainer>
                    </UspItem>
                )}
                {variant?.product?.itemPriceTagLinkUrl && (
                    <UspItem href={variant?.product?.itemPriceTagLinkUrl}>
                        <IconContainer>
                            {variant?.product?.itemPriceTagUrl ? (
                                <Image height={40} src={variant.product.itemPriceTagUrl} />
                            ) : (
                                <SvgIcon svg="tag" size={24} />
                            )}
                        </IconContainer>
                        <TextContainer column flex>
                            <Text fontWeight="Medium">{variant.product.itemPriceDescriptionText}</Text>
                            <Text
                                variant="caption"
                                color={theme.colors.textSubtle}
                                fontWeight="Medium"
                            >{`${variant.product.itemPriceLinkText}`}</Text>
                        </TextContainer>
                    </UspItem>
                )}
            </Container>
            <SidePanel
                show={showModal}
                variant="richtext"
                onChange={(change) => setShowModal(change)}
                title={modalTitle || undefined}
                modalTrackType={modalTrackType}
                mobileStyle
            >
                <RichText noPadding text={modalContent} />
            </SidePanel>
            <SidePanel
                show={showDeliveryModal}
                onChange={(change) => setShowDeliveryModal(change)}
                modalTrackType={ModalTrackingTypes.ProductPageDelivery}
                title={translate('productPage.delivery.panelTitle')}
                mobileStyle
            >
                <ProductDeliveryOptions
                    itemNumber={variant?.product?.itemNumber}
                    productPrice={variant?.activeAmountAsText}
                    onZipCodeSearch={onZipCodeChange}
                    selectedZipCode={selectedZipCode}
                    sameDayDeliveryData={sameDayDeliveryData}
                    validatingSameDayDelivery={validatingSameDayDelivery}
                    deliveryMethod={variant?.b2CDeliveryMethod}
                    defaultDeliveryText={
                        showDeliveryInformation ? deliveryText : showStoreInformation ? shopInformation : undefined
                    }
                />
            </SidePanel>
        </>
    );
};

const Container = styled.div<{ smallMargin?: boolean; borderColor?: string }>(
    ({ smallMargin, borderColor, theme }) => ({
        marginTop: smallMargin ? theme.space[6] : theme.space[9],
        border: theme.general.border,
        borderRadius: theme.general.borderRadius,
        ...(borderColor && { borderColor: borderColor }),
    })
);

const IconContainer = styled(Flex)({
    minWidth: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
});

const TextContainer = styled(Flex)(({ theme }) => ({
    paddingRight: theme.space[5],
}));
